import { z } from 'zod';
import { fuelSchema, originSchema, templateStatusSchema, versionStatusSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema, isoDateSchema, revisionSchema } from '../../common/schemas/money.schemas.js';
import { paginatedSchema, paginationInputSchema, sortDirectionSchema } from '../../common/schemas/pagination.schemas.js';
/**
 * Templates hold the **shared values** the equations read; Settings holds the equations themselves
 * (plan §3.2, PP §6). A template is per model and carries **no make-specific logic anywhere** — two
 * templates differ only by the numbers in their published version, which is what lets one insurance
 * formula produce two different caps (PP AC20, AC21).
 *
 * Percentages travel as **fractions** on the wire; only the UI ever shows a `%` (plan §11.6). The list
 * of keys, their groups, units, defaults and bounds is the engine's `TEMPLATE_PARAM_CATALOG`, served by
 * `templates.paramCatalog` — the contract states the shape, the engine states the catalogue, and the
 * two never disagree because nothing here repeats a key.
 */
/** The seven rows of one Arabic damage group, index 0 = "no finding" (plan §5.10). */
export const DAMAGE_GROUP_SIZE = 7;
export const damageTableSchema = z.array(amountSchema).length(DAMAGE_GROUP_SIZE, `A damage group has exactly ${DAMAGE_GROUP_SIZE} rows`);
/**
 * The full map of catalogue keys to values. It is a record rather than a fixed object because the
 * catalogue lives in the engine: a key added there must not need a contract release. The service
 * validates the map against the catalogue with `validateParams`, and a **missing key is an error** —
 * there is no silent fallback to a default (plan §10).
 */
export const templateParamsSchema = z.record(z.string(), z.number().finite());
export const templateNameSchema = z.string().trim().min(1, 'A template needs a name').max(120);
/** The identity half of a template: which vehicles it applies to. */
export const templateIdentitySchema = z.object({
    name: templateNameSchema,
    make: z.string().trim().min(1, 'Enter the make').max(80),
    model: z.string().trim().min(1, 'Enter the model').max(80),
    generation: z.string().trim().max(80).nullish(),
    trim: z.string().trim().max(80).nullish(),
    modelYearFrom: z.number().int().min(1900).max(2999).nullish(),
    modelYearTo: z.number().int().min(1900).max(2999).nullish(),
    fuel: fuelSchema,
    origin: originSchema.nullish(),
    /** The starter preset the first draft was pre-filled from; a label, never live logic (plan §3.2). */
    depreciationPreset: z.string().trim().max(64).nullish(),
    description: z.string().trim().max(2_000).nullish(),
});
export const templateSummarySchema = templateIdentitySchema.extend({
    id: uuidSchema,
    name: z.string(),
    make: z.string(),
    model: z.string(),
    status: templateStatusSchema,
    /** The version the estimator would apply today, or `null` while only a draft exists. */
    publishedVersion: z.number().int().min(1).nullable(),
    publishedVersionId: uuidSchema.nullable(),
    draftVersion: z.number().int().min(1).nullable(),
    draftVersionId: uuidSchema.nullable(),
    versionCount: z.number().int().min(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** A row of the Versions tab: everything except the values themselves. */
export const templateVersionSummarySchema = z.object({
    id: uuidSchema,
    templateId: uuidSchema,
    version: z.number().int().min(1),
    status: versionStatusSchema,
    referencePriceDefault: amountSchema.nullable(),
    referenceSource: z.string().nullable(),
    referenceDate: isoDateSchema.nullable(),
    reason: z.string().nullable(),
    publishedAt: z.string().datetime().nullable(),
    publishedBy: uuidSchema.nullable(),
    publishedByName: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    revision: revisionSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const templateVersionSchema = templateVersionSummarySchema.extend({
    params: templateParamsSchema,
    damageGroup1: damageTableSchema,
    damageGroup2: damageTableSchema,
    overridableKeys: z.array(z.string()),
});
/** `templates.get`: the template plus every version it has ever had, newest first. */
export const templateDetailSchema = templateSummarySchema.extend({
    versions: z.array(templateVersionSchema),
});
export const listTemplatesSortBySchema = z.enum(['name', 'make', 'model', 'updatedAt']);
export const listTemplatesInputSchema = paginationInputSchema.extend({
    sortBy: listTemplatesSortBySchema.default('name'),
    sortDirection: sortDirectionSchema.default('asc'),
    /** Free text over name, make and model. */
    q: z.string().trim().min(1).optional(),
    status: templateStatusSchema.optional(),
    fuel: fuelSchema.optional(),
    /** Archived templates are hidden from selection unless they are asked for (plan §6). */
    includeArchived: z.coerce.boolean().default(false),
});
export const listTemplatesOutputSchema = paginatedSchema(templateSummarySchema);
export const getTemplateInputSchema = z.object({
    templateId: uuidSchema,
});
/**
 * Creating a template also creates its **first DRAFT version**, pre-filled from the engine's
 * `paramsForPreset(depreciationPreset)` — or the catalogue defaults when no preset is named — with the
 * reference-price fields left empty for the person to confirm (plan §10).
 */
export const createTemplateInputSchema = templateIdentitySchema;
export const updateTemplateInputSchema = templateIdentitySchema.partial().extend({
    templateId: uuidSchema,
});
export const archiveTemplateInputSchema = z.object({
    templateId: uuidSchema,
});
export const createTemplateDraftVersionInputSchema = z.object({
    templateId: uuidSchema,
    /** Copy the values of this version; defaults to the published one ("Open as draft", plan §7). */
    fromVersionId: uuidSchema.optional(),
});
export const cloneTemplateVersionInputSchema = z.object({
    fromVersionId: uuidSchema,
    newTemplateName: templateNameSchema,
});
export const getTemplateVersionInputSchema = z.object({
    versionId: uuidSchema,
});
export const updateTemplateDraftVersionInputSchema = z.object({
    versionId: uuidSchema,
    expectedRevision: revisionSchema,
    referencePriceDefault: amountSchema.nullish(),
    referenceSource: z.string().trim().max(255).nullish(),
    referenceDate: isoDateSchema.nullish(),
    /** Every catalogue key, as fractions and amounts. A missing key is refused (plan §10). */
    params: templateParamsSchema,
    damageGroup1: damageTableSchema,
    damageGroup2: damageTableSchema,
    overridableKeys: z.array(z.string().trim().min(1)),
    reason: z.string().trim().max(2_000),
});
export const publishTemplateVersionInputSchema = z.object({
    versionId: uuidSchema,
    expectedRevision: revisionSchema,
    reason: z.string().trim().min(1, 'Explain the intended change'),
});
/**
 * The preview prices the **built-in reference sample** with this version's values through the tenant's
 * published policy (plan §7). The sample is deliberately the same vehicle for every template, so two
 * versions can be compared value for value.
 *
 * Passing `estimateId` prices **that estimate's own facts and inspection** with this candidate version
 * instead, which is how a salesperson sees what changing template would do to the vehicle in front of
 * them (plan §6, phase-2 note).
 */
export const previewTemplateVersionInputSchema = z.object({
    versionId: uuidSchema,
    estimateId: uuidSchema.optional(),
});
export const previewTemplateVersionOutputSchema = z.object({
    versionId: uuidSchema,
    version: z.number().int().min(1),
    policyVersionId: uuidSchema,
    policyVersion: z.number().int().min(1),
    /** Which vehicle was priced: the built-in reference sample, or a real estimate. */
    subject: z.enum(['SAMPLE', 'ESTIMATE']).default('SAMPLE'),
    estimateId: uuidSchema.nullish(),
    estimateNo: z.string().nullish(),
    sample: z.object({
        valuationYear: z.number().int(),
        modelYear: z.number().int(),
        referencePrice: amountSchema,
        odometerKm: z.number().min(0),
        ownerCount: z.number().int().min(0),
    }),
    outputs: z.object({
        appraisal: z.number().nullable(),
        ceiling: z.number().nullable(),
        retail: z.number().nullable(),
        insurance: z.number().nullable(),
        registration: z.number().nullable(),
        transfer: z.number().nullable(),
        holding: z.number().nullable(),
    }),
    /** Anything the run could not compute, so the page never shows a silently missing figure. */
    issues: z.array(z.string()),
});
export const paramGroupSchema = z.enum(['FEES', 'DEPRECIATION', 'INSPECTION', 'WORK', 'TRADE_IN']);
export const paramUnitSchema = z.enum(['JOD', 'km', 'L', 'days', 'years', 'fraction', 'count', 'flag', 'JOD/day', 'JOD/L', 'km/year']);
/** One catalogue entry, exactly as the engine declares it (plan §5.10). */
export const templateParamDefinitionSchema = z.object({
    key: z.string(),
    group: paramGroupSchema,
    label: z.string(),
    unit: paramUnitSchema,
    default: z.number(),
    min: z.number(),
    max: z.number(),
    integer: z.boolean(),
    provisional: z.boolean(),
    description: z.string().nullable(),
});
export const damageOptionSchema = z.object({
    index: z.number().int().min(0),
    arabic: z.string(),
    english: z.string(),
});
export const templateParamCatalogOutputSchema = z.object({
    params: z.array(templateParamDefinitionSchema),
    groups: z.array(paramGroupSchema),
    defaultOverridableKeys: z.array(z.string()),
    damageGroup1: z.array(damageOptionSchema),
    damageGroup2: z.array(damageOptionSchema),
});
/** The grade deductions a preset carries, keyed by سبعة جيد grade (`G7` … `G0`), as fractions. */
export const presetGradeRatesSchema = z.object({
    G7: z.number().finite().min(0).max(0.9),
    G4: z.number().finite().min(0).max(0.9),
    G3: z.number().finite().min(0).max(0.9),
    G2: z.number().finite().min(0).max(0.9),
    G1: z.number().finite().min(0).max(0.9),
    G0: z.number().finite().min(0).max(0.9),
});
/** Years 1–4 of depreciation, as fractions. */
export const presetRatesSchema = z.array(z.number().finite().min(0).max(0.9)).length(4, 'A preset has exactly four depreciation years');
/**
 * A starter preset **as this workspace keeps it** (business review 2026-09-23, plan §11.19). The four
 * presets start as the engine's `DEPRECIATION_PRESETS` and are then the workspace's own: an
 * administrator edits the depreciation years, the grade deductions and both damage tables, and may
 * write the new values into the drafts of chosen templates. A preset is still never a live link — a
 * template's *published* values change only when somebody publishes its draft.
 */
export const depreciationPresetSchema = z.object({
    key: z.string(),
    label: z.string(),
    rates: z.array(z.number()),
    gradeRates: presetGradeRatesSchema,
    damageGroup1: damageTableSchema,
    damageGroup2: damageTableSchema,
    /** The keys the preset pre-fills, so the confirm dialog can say how many values it replaces. */
    affectedKeys: z.array(z.string()),
    /** `null` while the preset still has the engine's values and nobody has edited it. */
    updatedAt: z.string().datetime().nullable(),
    updatedByName: z.string().nullable(),
    revision: revisionSchema,
});
export const depreciationPresetsOutputSchema = z.array(depreciationPresetSchema);
export const updateDepreciationPresetInputSchema = z.object({
    key: z.string().trim().min(1).max(64),
    expectedRevision: revisionSchema,
    rates: presetRatesSchema,
    gradeRates: presetGradeRatesSchema,
    damageGroup1: damageTableSchema,
    damageGroup2: damageTableSchema,
});
/**
 * Writes a preset's values into template **drafts** (plan §11.19). `ALL` means every active template;
 * `SELECTED` means the `templateIds` given. A template without a draft gets one opened from its
 * published version first. Nothing is published: each draft waits for its own explicit publish.
 */
export const applyDepreciationPresetInputSchema = z.object({
    key: z.string().trim().min(1).max(64),
    scope: z.enum(['ALL', 'SELECTED']),
    templateIds: z.array(uuidSchema).max(500).optional(),
    reason: z.string().trim().max(2_000).optional(),
});
export const presetApplicationRowSchema = z.object({
    templateId: uuidSchema,
    templateName: z.string(),
    draftVersionId: uuidSchema,
    draftVersion: z.number().int().min(1),
    /** True when the template had no draft and one was opened from its published version. */
    createdDraft: z.boolean(),
});
export const presetApplicationSkipSchema = z.object({
    templateId: uuidSchema,
    templateName: z.string(),
    reasonKey: z.string(),
});
export const applyDepreciationPresetOutputSchema = z.object({
    key: z.string(),
    applied: z.array(presetApplicationRowSchema),
    skipped: z.array(presetApplicationSkipSchema),
});
/**
 * "Also update the template's last price" from the estimator (plan §11.19). The value goes into the
 * template's **draft** — opened from the published version when there is none — so the live price every
 * other estimate reads only changes when somebody publishes it.
 */
export const setTemplateReferencePriceInputSchema = z.object({
    templateId: uuidSchema,
    referencePrice: amountSchema.positive(),
    /** Named in the activity row, so a publisher sees where the figure came from. */
    estimateId: uuidSchema.optional(),
});
export const setTemplateReferencePriceOutputSchema = z.object({
    templateId: uuidSchema,
    templateName: z.string(),
    draftVersionId: uuidSchema,
    draftVersion: z.number().int().min(1),
    createdDraft: z.boolean(),
    referencePrice: amountSchema,
});
//# sourceMappingURL=templates.schemas.js.map